import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type {
  IPReservadaInput, IPReservadaOut, PagedRedes, RedCreateInput, RedDetail, RedUpdateInput,
} from "./schemas";

const KEY = "redes";

export function useListRedes(filters: { page?: number; size?: number; search?: string }) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => {
      const p: Record<string, string | number> = {
        page: filters.page ?? 1, size: filters.size ?? 20,
      };
      if (filters.search) p.search = filters.search;
      return apiCall<PagedRedes>("get", "/api/v1/redes", { searchParams: p });
    },
    placeholderData: (prev) => prev,
  });
}

/** rc82 — IPs libres del pool de una red (picker de cambio de IP). */
export function useIpsDisponibles(redId: string | null, incluir?: string | null) {
  return useQuery({
    queryKey: [KEY, "ips-disponibles", redId, incluir],
    queryFn: () =>
      apiCall<{ ips: string[] }>("get", `/api/v1/redes/${redId}/ips-disponibles`, {
        searchParams: incluir ? { incluir } : {},
      }),
    enabled: !!redId,
  });
}

export function useRed(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => apiCall<RedDetail>("get", `/api/v1/redes/${id}`),
    enabled: !!id,
  });
}

export function useCreateRed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RedCreateInput) => apiCall<RedDetail>("post", "/api/v1/redes", { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useUpdateRed(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RedUpdateInput) => apiCall<RedDetail>("patch", `/api/v1/redes/${id}`, { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useDeleteRed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiCall<{ message: string }>("delete", `/api/v1/redes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useAddIPReservada(redId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IPReservadaInput) =>
      apiCall<IPReservadaOut>("post", `/api/v1/redes/${redId}/ips-reservadas`, { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useDeleteIPReservada(redId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ipId: string) =>
      apiCall<{ message: string }>("delete", `/api/v1/redes/${redId}/ips-reservadas/${ipId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

/** Preview de la próxima IP libre de una red (sin asignar). */
export function useNextIpRed(redId: string | null) {
  return useQuery({
    queryKey: [KEY, "next-ip", redId],
    queryFn: () => apiCall<{ ip: string }>("get", `/api/v1/redes/${redId}/next-ip`),
    enabled: !!redId,
    staleTime: 5_000, // IP preview — refresca rápido si cambia el pool
    retry: 0,
  });
}
